import { Navigate } from 'react-router-dom'

import AuthGuard from './components/guards/auth-guard'
import GuestGuard from './components/guards/guest-guard'
import OlGuard from './components/guards/ol-guard'
import { getUserDefaultRedirectionPath } from './components/pages/login/utils'
import { Paths } from './lib/constants/path'
import { useSelectedEnrollment } from './lib/contexts/selected-enrollment'
import useAuth from './lib/hooks/use-auth'
import { RoleEnum } from './lib/types/enums/users/role.enum'
import { Route } from './lib/types/routes'
import accountantPersonalSpaceRoutes from './views/accountant-personal-space/accountant-personal-space.routes'
import accountingsRoutes from './views/accountings/accountings.routes'
import authRoutes from './views/auth/auth.routes'
import AuthenticateTokenPage from './views/auth/authenticate-token'
import { LogoutPage } from './views/auth/logout'
import ResetPasswordPage from './views/auth/reset-password'
import budgetAllocationRoutes from './views/budget-allocation/budget-allocation.routes'
import { centerOlPaymentsRoutes } from './views/center-ol-payments/center-ol-payments.routes'
import { centerPaymentsRoutes } from './views/center-payments/center-payments.routes'
import centerRoutes from './views/centers/center.routes'
import clientPersonalSpaceRoutes from './views/client-personal-space/client-personal-space.routes'
import ClientRedirectionPage from './views/client-personal-space/client-redirection'
import ClientPageNotFound from './views/common/client-page-not-found'
import NotFoundPage from './views/common/not-found'
import countryRoutes from './views/countries/country.routes'
import { cseRoutes } from './views/cse/cse.routes'
import { EnrollmentCseValidationPage } from './views/cse/enrollment-cse-validation'
import enrollmentCharacteristicsRoutes from './views/enrollment-characteristics/enrollment-characteristics.routes'
import aAKeyWordRoutes from './views/enrollments/aa-keywords/aa-keyword.routes'
import { canGoOutAloneConditionsRoutes } from './views/enrollments/can-go-out-alone-conditions/can-go-out-alone-conditions.routes'
import enrollmentsRoutes from './views/enrollments/enrollment/enrollment.routes'
import enrollmentQuotationRoutes from './views/enrollments/enrollment-quotation/enrollment-quotation.routes'
import { insuranceRoutes } from './views/enrollments/insurance/enrollment-quotation.routes'
import medicalTermRoutes from './views/enrollments/medical-terms/medical-term.routes'
import promoCodeRoutes from './views/enrollments/promo-codes/promo-code.routes'
import guideRoutes from './views/guides/guide.routes'
import howDidYouObtainLecBrochuresRoutes from './views/how-did-you-obtain-lec-brochures/how-did-you-obtain-lec-brochures.routes'
import informationRequestRoutes from './views/information-requests/information-request.routes'
import journeyPlaceRoutes from './views/journey-places/journey-place.routes'
import journeyRoutes from './views/journeys/journey.routes'
import { languageTestRoutes } from './views/language-test/routes'
import lecBankAccountRoutes from './views/lec-bank-accounts/lec-bank-accounts.routes'
import missionTrackingRoutes from './views/mission-trackings/mission-tracking.routes'
import missionRoutes from './views/missions/mission.routes'
import olPersonalSpaceRoutes from './views/ol-personal-space/ol-personal-space.routes'
import olRoutes from './views/ols/ol.routes'
import otoIndividualPlacesRoutes from './views/oto-individual-places/oto-individual-places.routes'
import paymentConditionRoutes from './views/payment-conditions/payment-conditions.routes'
import pcrPersonalSpaceRoutes from './views/pcr-personal-space/pcr-personal-space.routes'
import productRoutes from './views/products/product.routes'
import radRoutes from './views/rads/rad.routes'
import formulaWishListRoutes from './views/settings/formula-wish-list/formula.routes'
import hobbyRoutes from './views/settings/hobbies/hobby.routes'
import howDidYouHearAboutUsRoutes from './views/settings/how-did-you-hear-about-us/how-did-you-hear-about-us.routes'
import periodRoutes from './views/settings/periods/period.routes'
import staffCommentSubjectRoutes from './views/settings/staff-comment-subjects/staff-comment-subjects.routes'
import transportMeanRoutes from './views/settings/transport-means/transport-mean.routes'
import travelCompanyRoutes from './views/settings/travel-companies/travel-companies.routes'
import tripPlaceRoutes from './views/settings/trip-places/trip-places.routes'
import staffApplicationRoutes from './views/staff-applications/staff-application.routes'
import staffPersonalSpaceRoutes from './views/staff-personal-space/staff-personal-space.routes'
import staffRoutes from './views/staffs/staff.routes'
import { endOfStayAppreciationsSummaryRoutes } from './views/surveys/end-of-stay-appreciations-summary/routes'
import { endOfStayTemplateRoutes } from './views/surveys/end-of-stay-templates/routes'
import systemAdminRoutes from './views/system-admin/system-admin.routes'
import transferRoutes from './views/transfers/transfer.routes'
import travelExpensesRoutes from './views/travel-expenses/travel-expenses.routes'
import tripAddressRoutes from './views/trip-addresses/trip-address.routes'
import tripBookingGuidelineRoutes from './views/trip-booking-guidelines/trip-booking-guideline.routes'
import { missionTypeRoutes } from './views/trip-bookings/missions/mission-type.routes'
import tripBookingRoutes from './views/trip-bookings/trip-booking.routes'
import { tripDatesRoutes } from './views/trip-bookings/trip-dates/trip-dates.routes'
import { tripStepsRoutes } from './views/trip-bookings/trip-steps/trip-steps.routes'
import tripFolderRoutes from './views/trip-folder/trip-folder.routes'
import tripGroupingRoutes from './views/trip-groupings/trip-grouping.routes'
import tripProviderRoutes from './views/trip-providers/trip-provider.routes'
import tripRoutes from './views/trips/trip.routes'
import tripsMonitoringRoutes from './views/trips-monitoring/trips-monitoring.routes'
import uiRoutes from './views/ui/ui.routes'
import youthAndSportNumberRoutes from './views/youth-and-sport-numbers/youth-and-sport-number.routes'

const protectedRoutes: Route[][] = [
  systemAdminRoutes,
  staffApplicationRoutes,
  staffPersonalSpaceRoutes,
  pcrPersonalSpaceRoutes,
  transportMeanRoutes,
  howDidYouHearAboutUsRoutes,
  informationRequestRoutes,
  staffCommentSubjectRoutes,
  travelCompanyRoutes,
  missionTrackingRoutes,
  hobbyRoutes,
  periodRoutes,
  tripPlaceRoutes,
  staffRoutes,
  countryRoutes,
  journeyPlaceRoutes,
  formulaWishListRoutes,
  tripRoutes,
  radRoutes,
  tripGroupingRoutes,
  productRoutes,
  journeyRoutes,
  olRoutes,
  cseRoutes,
  tripBookingRoutes,
  tripProviderRoutes,
  tripBookingGuidelineRoutes,
  tripAddressRoutes,
  enrollmentQuotationRoutes,
  enrollmentsRoutes,
  insuranceRoutes,
  promoCodeRoutes,
  aAKeyWordRoutes,
  medicalTermRoutes,
  missionRoutes,
  tripStepsRoutes,
  missionTypeRoutes,
  missionRoutes,
  canGoOutAloneConditionsRoutes,
  centerRoutes,
  lecBankAccountRoutes,
  centerOlPaymentsRoutes,
  centerPaymentsRoutes,
  uiRoutes,
  paymentConditionRoutes,
  tripDatesRoutes,
  tripFolderRoutes,
  transferRoutes,
  guideRoutes,
  clientPersonalSpaceRoutes,
  languageTestRoutes,
  enrollmentCharacteristicsRoutes,
  howDidYouObtainLecBrochuresRoutes,
  budgetAllocationRoutes,
  travelExpensesRoutes,
  tripsMonitoringRoutes,
  accountantPersonalSpaceRoutes,
  accountingsRoutes,
  endOfStayTemplateRoutes,
  endOfStayAppreciationsSummaryRoutes,
  youthAndSportNumberRoutes,
  otoIndividualPlacesRoutes
]

const Placeholder = () => {
  const { user } = useAuth()
  const { selectedEnrollmentId } = useSelectedEnrollment()

  return user ? (
    <Navigate to={getUserDefaultRedirectionPath(user, selectedEnrollmentId)} />
  ) : (
    <Navigate to={Paths.AUTH_LOGIN_PAGE} />
  )
}

const routes: Route[] = [
  {
    element: <AuthGuard />,
    children: [
      ...protectedRoutes.flat(),
      {
        element: <OlGuard />,
        children: [...olPersonalSpaceRoutes]
      },
      {
        path: Paths.AUTH_LOGOUT_PAGE,
        element: <LogoutPage />
      }
    ]
  },
  {
    element: <GuestGuard />,
    children: [
      ...authRoutes,
      {
        path: Paths.ENROLLMENT_ACCEPT_CSE_PAGE,
        element: <EnrollmentCseValidationPage mode="accept" />
      },
      {
        path: Paths.ENROLLMENT_REJECT_CSE_PAGE,
        element: <EnrollmentCseValidationPage mode="reject" />
      }
    ]
  },
  {
    path: Paths.CLIENT_PERSONAL_SPACE_REDIRECTION,
    element: <ClientRedirectionPage />,
    roles: [RoleEnum.CLIENT]
  },
  {
    path: Paths.AUTH_RESET_PASSWORD_PAGE,
    element: <ResetPasswordPage />
  },
  {
    path: Paths.AUTH_AUTHENTICATE_TOKEN,
    element: <AuthenticateTokenPage />
  },
  {
    path: '/',
    element: <Placeholder />
  },
  { path: 'client-page-not-found', element: <ClientPageNotFound /> },
  { path: '*', element: <NotFoundPage /> }
]

export default routes
